import { $, _, Backbone } from "js/vendor";
import { ds } from "js/core/models/dataService";
import log from "js/core/logger";
import { app } from "js/namespaces.js";
import { controls } from "js/editor/ui";
import * as geom from "js/core/utilities/geom";
import { Key } from "js/core/utilities/keys.js";
import { ArrowDirection } from "common/constants";
import { slideTemplates } from "js/core/slide_templates/slide_template";
import ErrorHandler from "js/core/utilities/errorHandler";
import { trackActivity } from "js/core/utilities/utilities";
import { FeatureType } from "js/core/models/features";
import { ExportToPPT } from "js/exporter/exportToPPT";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import React, { setGlobal } from "reactn";
import {
    ShowDialog,
    ShowUpgradeDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import { AddSlideContainer } from "js/react/views/AddSlide";
import Api from "js/core/api";

const SlideGrid = Backbone.View.extend({

    className: "slide-grid overlay--arrange-presentation-slides",
    EVENT_NAMESPACE: ".slidegrid",
    DRAGGING_EVENT_NAMESPACE: ".slidegrid_dragging",

    initialize: function(options) {
        this.editorView = options.editorView;
        this.isVisible = false;

        $(window).on("keydown" + this.EVENT_NAMESPACE, event => this.handleKeyShortcuts(event));

        this.listenTo(ds.selection.presentation, "slidesUpdated", () => {
            setGlobal({
                selectionPresentationSlideLength: ds.selection.presentation.slides.length
            });
            this.updateSlides();
        });
        this.listenTo(ds.selection.presentation, "slideorder", this.updateSlides);

        this.slideThumbnails = [];
        this.selectedSlides = [];

        $(window).on("resize" + this.EVENT_NAMESPACE, () => this.layout(false));
    },

    remove: function() {
        this.slideThumbnails.forEach(slideThumbnail => slideThumbnail.remove());
        Backbone.View.prototype.remove.apply(this, arguments);
        $(window).off(this.DRAGGING_EVENT_NAMESPACE).off(this.EVENT_NAMESPACE);
    },

    render: function() {
        this.$slides = this.$el.addEl($.div("slides"));
        this.$slides.on("mousedown", event => {
            this.onDrawRectStart(event);
        });

        this.dragRect = this.$slides.addEl($.div("drag-rect"));

        this.updateSlides();

        this.selectSlide(this.getSlideThumbnail(ds.selection.slide));

        return this;
    },

    onBecameVisible: function() {
        this.isVisible = true;
        this.slideThumbnails.forEach(slideThumbnail => slideThumbnail.onSlideGridBecameVisible());
    },

    scrollToFocusedSlide: function() {
        this.$el.find(".slides").scrollTop(this.focusedSlide.y - 10);
    },

    getSlideThumbnail: function(slideModel) {
        return _.find(this.slideThumbnails, thumb => thumb.model.id == slideModel.id);
    },

    updateSlides: function() {
        for (let slide of ds.selection.presentation.slides.models) {
            let thumbnail = this.getSlideThumbnail(slide);
            if (!thumbnail) {
                thumbnail = this.createSlideThumbnail(slide);
                this.$slides.append(thumbnail.render().$el);
                if (this.isVisible) {
                    thumbnail.onSlideGridBecameVisible();
                }
                this.setupDragging(thumbnail);
            }
        }

        let removedThumbnails = [];
        for (let slideThumbnail of this.slideThumbnails) {
            if (!_.find(ds.selection.presentation.slides.models, model => model.id == slideThumbnail.model.id)) {
                slideThumbnail.$el.remove();
                removedThumbnails.push(slideThumbnail);
            }
        }

        for (let slideThumbnail of removedThumbnails) {
            this.slideThumbnails.remove(slideThumbnail);
        }

        this.slideThumbnails = _.sortBy(this.slideThumbnails, thumb => thumb.getSlideIndex());
        this.layout();

        if (this.slideThumbnails.some(({ model }) => !model.loaded)) {
            // If some of the slide models aren't loaded then load the metadata
            // to allow correnctly loading thumbnails and rendering skipped marks
            Api.presentationSlidesMetadata.get({ id: ds.selection.presentation.id })
                .then(slidesMetadata =>
                    this.slideThumbnails
                        .forEach(thumbnail =>
                            thumbnail.setSlideMetadata(slidesMetadata[thumbnail.model.id])
                        )
                );
        }
    },

    getFocusedSlidePosition: function() {
        if (this.focusedSlide) {
            return new geom.Point(this.focusedSlide.$el.left(), this.focusedSlide.$el.top());
        }
    },

    handleKeyShortcuts: function(event) {
        //Protect against input fields so that we don't accidentally trigger these key events.
        if (event.target.tagName === "INPUT") {
            return;
        }
        let nextIndex = null;
        event.stopPropagation();
        switch (event.which) {
            case Key.KEY_Z:
                if (event.metaKey || event.ctrlKey) {
                    if (event.shiftKey) {
                        app.undoManager.redo();
                    } else {
                        app.undoManager.undo();
                    }
                    event.preventDefault();
                    return;
                }
                break;
            // case Key.SPACE:
            //     this.closeSlideGrid();
            //     break;
            case Key.ESCAPE:
                if (!this.isDraggingRect && !this.isDragging && !this.editorView.isPresenting) {
                    this.closeSlideGrid();
                }
                return;
            case Key.LEFT_ARROW:
                nextIndex = this.moveSlideSelection(ArrowDirection.LEFT, event.shiftKey);
                break;
            case Key.RIGHT_ARROW:
                nextIndex = this.moveSlideSelection(ArrowDirection.RIGHT, event.shiftKey);
                break;
            case Key.UP_ARROW:
                nextIndex = this.moveSlideSelection(ArrowDirection.UP, event.shiftKey);
                break;
            case Key.DOWN_ARROW:
                nextIndex = this.moveSlideSelection(ArrowDirection.DOWN, event.shiftKey);
                break;
            case Key.SPACE:
                if (!this.isDraggingRect && !this.isDragging && !this.editorView.isPresenting) {
                    this.closeSlideGrid();
                }
                break;
        }
    },

    moveSlideSelection: function(direction, addToSelection) {
        let start = this.slideThumbnails.indexOf(this.focusedSlide);

        let nextIndex;

        switch (direction) {
            case ArrowDirection.LEFT:
                if (start > 0) {
                    nextIndex = start - 1;
                } else {
                    nextIndex = this.slideThumbnails.length - 1;
                }
                break;
            case ArrowDirection.RIGHT:
                if (start < ds.selection.presentation.slides.length - 1) {
                    nextIndex = start + 1;
                } else {
                    nextIndex = 0;
                }
                break;
            case ArrowDirection.DOWN:
                nextIndex = start + this.totalCols;
                if (nextIndex >= this.slideThumbnails.length) {
                    nextIndex = 0;
                }
                break;
            case ArrowDirection.UP:
                nextIndex = start - this.totalCols;
                if (nextIndex < 0) {
                    nextIndex = this.slideThumbnails.length - 1;
                }
                break;
        }
        this.selectSlide(this.slideThumbnails[nextIndex]);
        this.scrollToFocusedSlide();
    },

    setupDragging: function(thumbnail) {
        let gridWidth, totalCols;

        thumbnail.$el.draggable({
            distance: 5,
            containment: this.$el.find(".slides"),
            start: (event, ui) => {
                gridWidth = this.$el.find(".slides").innerWidth();
                // let totalCols = Math.floor((window.innerWidth - this.LEFT_OFFSET - this.PADDING) / (this.colWidth + this.gridGap));
                totalCols = Math.floor(gridWidth / (this.colWidth + this.gridGap));

                // this.draggedSlides = [];
                for (let slide of this.selectedSlides) {
                    // this.draggedSlides.push(slide);
                    // this.slideThumbnails.remove(slide);
                    slide.$el.addClass("isDragging");
                    slide.isDragging = true;
                    if (slide != thumbnail) {
                        slide.$el.left(thumbnail.$el.left()).top(thumbnail.$el.top()).css("transform", `rotate(${(Math.random() * 10) - 5}deg)`).addClass("isDragFollow");
                    }
                }
            },
            drag: (event, ui) => {
                let col = Math.clamp(Math.floor((ui.position.left + this.colWidth / 2) / (this.colWidth + this.gridGap)), 0, totalCols);
                let row = Math.floor((ui.position.top + this.rowHeight / 2) / (this.rowHeight + this.gridGap));

                this.dropTargetIndex = row * totalCols + col;

                for (let slide of this.selectedSlides) {
                    if (slide != thumbnail) {
                        slide.$el.left(thumbnail.$el.left()).top(thumbnail.$el.top());
                    }
                }

                this.layout();
            },
            stop: (event, ui) => {
                for (let slide of this.selectedSlides) {
                    slide.$el.removeClass("isDragging isDragFollow");
                    slide.isDragging = false;
                }
                this.moveSlides(this.selectedSlides, this.dropTargetIndex);
                this.dropTargetIndex = null;
            }
        });
        thumbnail.$el.css("position", "absolute");
    },

    createSlideThumbnail: function(slide) {
        const slideThumbnail = new SlideGridThumbnail({
            grid: this,
            slide: slide
        });
        this.slideThumbnails.push(slideThumbnail);
        return slideThumbnail;
    },

    gridGap: 30,
    THUMB_WIDTH: 200,
    LEFT_OFFSET: 259,
    PADDING: 80,

    layout: function(transition = true) {
        let x = 4;
        let y = 4;

        // 8 represents double x's value above, 250 represents the left menu bar width,
        // 80 represents the 40px left and right gaps of .slides
        let width = window.innerWidth - this.LEFT_OFFSET - this.PADDING - x * 2;

        let colCount = Math.floor(width / this.THUMB_WIDTH);
        if (colCount < 4) {
            colCount = Math.floor(width / 170);
        }

        this.colWidth = (width - (this.gridGap * (colCount - 1))) / colCount;
        //(9 / 16) is the aspect ratio of slide thumb, 10 is for padding between slide thumb and slide number
        this.rowHeight = this.colWidth * 9 / 16 + 10;
        this.totalCols = colCount;

        let drawThumbs = _.filter(this.slideThumbnails, slide => !slide.isDragging);
        if (this.dropTargetIndex != null) {
            drawThumbs.insert("drop", this.dropTargetIndex);
        }

        for (let thumb of drawThumbs) {
            if (thumb instanceof SlideGridThumbnail) {
                if (transition) {
                    thumb.$el.css("transition", "400ms");
                } else {
                    thumb.$el.css("transition", "0ms");
                }
                thumb.$el.left(x).top(y).css("transform", "rotate(0deg)").width(this.colWidth).height(this.rowHeight);
                thumb.y = y;
                thumb.layout();
            }

            x += this.colWidth;

            if (x + this.colWidth > width) {
                x = 4;
                y += this.rowHeight + this.gridGap;
            } else {
                x += this.gridGap;
            }
        }
    },

    selectSlide: function(slide, metaKey, shiftKey, force) {
        if (force) {
            this.selectedSlides = [slide];
            this.focusedSlide = slide;
        } else if (this.selectedSlides.contains(slide) && !metaKey) {
            return;
        } else if ((!metaKey && !shiftKey) || !this.focusedSlide) {
            this.focusedSlide = slide;
            this.selectedSlides = [this.focusedSlide];
        } else if (shiftKey) {
            this.selectedSlides = [];
            for (let i = Math.min(this.focusedSlide.getSlideIndex(), slide.getSlideIndex()); i <= Math.max(this.focusedSlide.getSlideIndex(), slide.getSlideIndex()); i++) {
                this.selectedSlides.push(this.slideThumbnails[i]);
            }
        } else if (metaKey) {
            if (this.selectedSlides.contains(slide)) {
                this.selectedSlides.remove(slide);
                if (this.focusedSlide == slide) {
                    if (this.selectedSlides.length) {
                        this.focusedSlide = this.selectedSlides[0];
                    } else {
                        this.focusedSlide = slide;
                        this.selectedSlides = [slide];
                    }
                }
            } else {
                this.selectedSlides.push(slide);
            }
        }

        this.renderSelection();
    },

    renderSelection: function() {
        this.$el.find(".slide-thumbnail").removeClass("selected").removeClass("focused");
        for (let selectedSlide of this.selectedSlides) {
            selectedSlide.$el.addClass("selected");
        }
        this.focusedSlide && this.focusedSlide.$el.addClass("focused");
    },

    closeSlideGrid: function() {
        this.trigger("close");
    },

    handleSkippedInContextMenu: function() {
        const isFocusSlideSkipped = this.focusedSlide.model.get("isSkipped");
        let skipSlideOption = {
            icon: isFocusSlideSkipped ? "visibility" : "visibility_off",
            value: isFocusSlideSkipped ? "unskip" : "skip"
        };
        if (isFocusSlideSkipped) {
            skipSlideOption.label = (this.selectedSlides.length != 1) ? `Unskip ${this.selectedSlides.length} Slides` : "Unskip Slide";
        } else {
            skipSlideOption.label = (this.selectedSlides.length != 1) ? `Skip ${this.selectedSlides.length} Slides` : "Skip Slide";
        }
        return skipSlideOption;
    },

    renderExportToJpegInContextMenu: function() {
        const workspaceId = ds.selection.presentation.getWorkspaceId();
        if (this.selectedSlides.length === 1) {
            return [
                {
                    type: "divider"
                }, {
                    label: "Export To JPEG",
                    icon: "panorama",
                    value: "export_to_jpeg"
                }, {
                    label: "Export To PPTX",
                    icon: "cloud_download",
                    value: "export_to_pptx",
                    showProBadge: !app.user.features.isFeatureEnabled(FeatureType.DESKTOP_APP, workspaceId),
                    proBadgeAnalytics: { cta: "exportToPPT", ...ds.selection.presentation.getAnalytics() },
                    proBadgeWorkspaceId: workspaceId
                }
            ];
        }
        return [];
    },

    // region Context Menu

    showContextMenu: function(event, slideEl) {
        const skipSlideOption = this.handleSkippedInContextMenu();
        const items = [
            {
                label: "Add Slide",
                icon: "add",
                value: "add_slide"
            },
            skipSlideOption,
            {
                type: "divider"
            },
            {
                label: this.selectedSlides.length != 1 ? `Duplicate ${this.selectedSlides.length} Slides` : "Duplicate Slide",
                icon: "content_copy",
                value: "duplicate"
            },
            {
                label: this.selectedSlides.length != 1 ? `Delete ${this.selectedSlides.length} Slides` : "Delete Slide",
                icon: "delete",
                value: "delete"
            },
            {
                type: "divider"
            },
            {
                label: "Select All",
                icon: "select_all",
                value: "select_all"
            },
            ...this.renderExportToJpegInContextMenu()
        ];

        controls._createMenu(slideEl, {
            items: items,
            callback: this.optionsMenuClick.bind(this),
            overwriteTriggerHandler: true,
            preventBlurOnMenuClose: true
        });
    },

    optionsMenuClick: function(type) {
        switch (type) {
            case "add_slide":
                ShowDialog(AddSlideContainer);
                break;
            case "duplicate":
                this.duplicateSlides();
                break;
            case "delete":
                this.deleteSlides();
                break;
            case "skip":
                this.skipSlides();
                break;
            case "unskip":
                this.unskipSlides();
                break;
            case "export_to_jpeg":
                this.exportSlideToJpeg();
                break;
            case "export_to_pptx":
                this.exportSlideToPPT();
                break;
            case "select_all":
                this.selectedSlides = [];
                this.focusedSlide = _.first(this.slideThumbnails);
                this.selectSlide(_.last(this.slideThumbnails), false, true);
                break;
        }
    },

    // endregion

    // region Update Slide Models

    moveSlides: function(slides, index) {
        app.undoManager.openGroup();
        ds.selection.presentation.moveSlides(_.map(slides, slide => slide.model.id), index).then(() => {
            app.undoManager.closeGroup();
        });
    },

    duplicateSlides() {
        app.undoManager.openGroup();
        let slides = ds.selection.presentation.sortSlides(_.map(this.selectedSlides, slide => slide.model), slide => slide.id);
        const workspaceId = ds.selection.presentation.getWorkspaceId();
        const insertIndex = _.max(this.selectedSlides.map(slide => slide.model.getIndex())) + 1;
        ds.selection.presentation.batchDuplicateSlides(slides, { silent: true, insertIndex }).then(slideModels => {
            app.undoManager.closeGroup();
            this.selectSlide(this.getSlideThumbnail(slideModels[0]), false, false, true);
            const duplicateSlideCount = slideModels.length;
            const eventProps = {
                "slide_ids": slideModels.map(s => s.get("id")),
                "source_slides": slides.map(s => s.get("id")),
                "slide_template_names": slideModels.map(s => slideTemplates[s.get("template_id")].title),
                "slides_created": duplicateSlideCount,
            };
            trackActivity("Slide", "Duplicate", null, duplicateSlideCount, eventProps, { audit: true });
        }).catch(err => ErrorHandler.handleSlideLimitReached(err, { workspaceId }));
    },

    deleteSlides() {
        let deleteSlides = _.clone(this.selectedSlides);
        // if trying to delete all slides, keep first slide
        if (this.selectedSlides.length === ds.selection.presentation.slides.length) {
            deleteSlides = deleteSlides.slice(1);
        }

        let firstSlideIndex = deleteSlides[0].getSlideIndex();

        app.undoManager.openGroup();
        ds.selection.presentation.destroySlides(deleteSlides.map(slide => slide.model.id)).then(() => {
            app.undoManager.closeGroup();
            this.selectSlide(this.slideThumbnails[Math.max(0, firstSlideIndex - 1)], false, false, true);
            this.editorView.updateSlideInfo(this.editorView.getCurrentSlideIndex());
        });
    },

    skipSlides: function() {
        const slideModels = this.selectedSlides.map(slide => slide.model);
        const sharedSlideModels = slideModels.filter(slideModel => slideModel.has("libraryItemId"));
        const nonSharedSlideModels = slideModels.filter(slideModel => !slideModel.has("libraryItemId"));
        if (sharedSlideModels.length) {
            return ShowWarningDialog({
                title: "Sorry, Team Slides are not skippable.",
                message: "Please remove the team slide from your presentation instead of skipping.",
                buttonText: "Got it"
            });
        }

        ds.selection.presentation.batchSkipSlides(slideModels);
    },

    unskipSlides: function() {
        const slideModels = this.selectedSlides.map(slide => slide.model);
        const sharedSlideModels = slideModels.filter(slideModel => slideModel.has("libraryItemId"));
        const nonSharedSlideModels = slideModels.filter(slideModel => !slideModel.has("libraryItemId"));
        if (sharedSlideModels.length) {
            return ShowWarningDialog({
                title: "Sorry, Team Slides are not unskippable.",
                buttonText: "Got it"
            });
        }

        ds.selection.presentation.batchUnskipSlides(slideModels);
    },

    // endregion

    // region BoxSelect

    onDrawRectStart: function(event) {
        let scrollbarWidth = $.getScrollBarWidth() || 16; // 16 is for hovering scrollbars in osx which can't be measured
        if ($(event.target).hasScroll() && window.outerWidth - event.clientX < scrollbarWidth) {
            return;
        }
        this.drawRectStartEvent = event;
        this.isDraggingRect = false;
        this.dragRect.show().css({
            "left": event.pageX - this.$el.find(".slides").offset().left,
            "top": event.pageY - this.$el.find(".slides").offset().top,
            "width": 0,
            "height": 0
        });

        this.selectedSlides = [this.focusedSlide];
        this.renderSelection();

        this.hasToggled = [];
        $(window).off(this.DRAGGING_EVENT_NAMESPACE).on("mousemove" + this.DRAGGING_EVENT_NAMESPACE, this.onDrawRectMove.bind(this)).on("mouseup" + this.DRAGGING_EVENT_NAMESPACE, this.onDrawRectEnd.bind(this));
    },

    onDrawRectMove: function(event) {
        const slideOffset = this.$slides.offset();

        let x1 = this.drawRectStartEvent.pageX,
            y1 = this.drawRectStartEvent.pageY,
            x2 = event.originalEvent.pageX,
            y2 = event.originalEvent.pageY,
            tmp;

        if (!this.isDraggingRect && Math.abs(x1 - x2) <= 5 || Math.abs(y1 - y2) <= 5) {
            return;
        }

        this.isDraggingRect = true;

        if (x1 > x2) {
            tmp = x2;
            x2 = x1;
            x1 = tmp;
        }
        if (y1 > y2) {
            tmp = y2;
            y2 = y1;
            y1 = tmp;
        }
        this.dragRect.css({
            left: x1 - slideOffset.left,
            top: y1 - slideOffset.top + this.$slides.scrollTop(),
            width: x2 - x1,
            height: y2 - y1
        });

        let selectedSlides = this.slideThumbnails.filter(thumb => {
            let thumbRect = thumb.el.getBoundingClientRect();
            return !(thumbRect.left > x2 || thumbRect.right < x1 || thumbRect.top > y2 || thumbRect.bottom < y1);
        });

        if (selectedSlides.length) {
            this.selectedSlides = selectedSlides;
            this.focusedSlide = selectedSlides[0];
            this.renderSelection();
        }
    },

    onDrawRectEnd: function(event) {
        this.dragRect.hide();
        $(window).off(this.DRAGGING_EVENT_NAMESPACE);
        this.drawRectStartEvent = null;
        this.isDraggingRect = false;
    },

    // endregion

    exportSlideToJpeg() {
        this.focusedSlide.model.exportSlideToJpeg(this.$el, this.focusedSlide.$el);
    },

    exportSlideToPPT() {
        const workspaceId = ds.selection.presentation.getWorkspaceId();
        if (app.user.features.isFeatureEnabled(FeatureType.DESKTOP_APP, workspaceId)) {
            const exporter = new ExportToPPT();
            exporter.export({
                slideCanvases: [app.mainView.editorView.getSlideByModel(this.focusedSlide.model).canvas],
                includeSkippedSlides: true
            });
        } else {
            ShowUpgradeDialog({
                type: UpgradePlanDialogType.UPGRADE_PLAN,
                analytics: { cta: "exportToPPT", ...ds.selection.presentation.getAnalytics() },
                workspaceId
            });
        }
    }

});

const SlideGridThumbnail = Backbone.View.extend({
    className: "slide-thumbnail",

    events: {
        "mousedown": "onMouseDown",
        "mouseup": "onMouseUp",
        "dblclick": "onDoubleClick",
        "contextmenu": "onContextMenu"
    },

    initialize: function(options) {
        this.grid = options.grid;
        this.model = options.slide;
        this.isDragging = false;
        this.isVisible = false;
    },

    getSlideIndex: function() {
        return ds.selection.presentation.getSlideIndex(this.model.id);
    },

    renderSkip: function(elem) {
        elem.addClass("thumbnail-skip-corner");
        elem.prepend($.icon("visibility_off", "thumbnail-skip"));
    },

    renderAssignedToCurrentUserIcon: function(elem) {
        const assignedToUserCorner = elem.addEl($.div("assigned-to-user-container"));
        assignedToUserCorner.append($.icon("person_pin", "assigned-to-user"));
    },

    hideAssignedToCurrentUserIcon: function(elem) {
        elem.find(".assigned-to-user-container").remove();
    },

    hideSkip: function(elem) {
        elem.removeClass("thumbnail-skip-corner");
        elem.find(".thumbnail-skip").remove();
    },

    setSlideMetadata: function(slideMetadata) {
        this.slideMetadata = slideMetadata;
        if (this.model.loaded) {
            // No need to do anything
            return;
        }

        if (this.slideMetadata.isSkipped) {
            this.renderSkip(this.$frame);
        }

        if (this.isVisible) {
            // Rendering thumbnail once metadata is received
            this.renderThumbnail();
        }
    },

    renderThumbnail: function() {
        this.$frame.spinner(true);
        this.$frame.addClass("loading");

        return this.model.getThumbnailUrl(ds.selection.presentation.id, "small", 0, true, this.slideMetadata?.modifiedAt)
            .then(url => {
                this.$el.append($.img(url, this.model.get("template_id")));
                this.$frame.spinner(false);
                this.$frame.removeClass("loading");

                if ((this.model.loaded && this.model.get("isSkipped")) ||
                    (!this.model.loaded && this.slideMetadata?.isSkipped)) {
                    this.renderSkip(this.$frame);
                }

                if (this.model.get("assignedUser") === app.user.id) {
                    this.renderAssignedToCurrentUserIcon(this.$frame);
                }
            }).catch(err => {
                log.error(err);

                this.$frame.spinner(true);
                this.$frame.addClass("loading");
            });
    },

    render: function() {
        this.$el.data("id", this.model.id);

        this.$frame = this.$el.addEl($.div("frame"));
        this.$frame.spinner(true);
        this.$frame.addClass("loading");

        this.listenTo(this.model, "change:modifiedAt",
            () => {
                if (!this.isVisible) {
                    return;
                }

                this.renderThumbnail()
                    .then(() => {
                        if (this.model.get("isSkipped")) {
                            this.renderSkip(this.$frame);
                        }

                        if (this.model.get("assignedUser") === app.user.id) {
                            this.renderAssignedToCurrentUserIcon(this.$frame);
                        }
                    });
            });

        this.listenTo(this.model, "change:isSkipped",
            () => {
                if (!this.isVisible) {
                    return;
                }

                if (this.model.get("isSkipped")) {
                    this.renderSkip(this.$frame);
                } else {
                    this.hideSkip(this.$frame);
                }
            });

        this.listenTo(this.model, "change:assignedUser", () => {
            if (!this.isVisible) {
                return;
            }

            if (this.model.get("assignedUser") === app.user.id) {
                this.renderAssignedToCurrentUserIcon(this.$frame);
            } else {
                this.hideAssignedToCurrentUserIcon(this.$frame);
            }
        });

        this.$el.append($.div("slide-num", 1));
        this.$collaborators = this.$el.addEl($.div("collaboration-view"));
        return this;
    },

    onSlideGridBecameVisible: function() {
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting === true) {
                observer.disconnect();
                this.isVisible = true;

                if (this.model.loaded || this.slideMetadata) {
                    this.renderThumbnail();
                }
            }
        });

        observer.observe(this.$el[0], { root: this.grid.$slides[0] });
    },

    layout: function() {
        this.$el.find(".slide-num").text(this.getSlideIndex() + 1);
    },

    onMouseDown: function(event) {
        event.stopPropagation();
        this.grid.selectSlide(this, event.metaKey || event.ctrlKey, event.shiftKey);
    },

    onMouseUp: function(event) {
        if (!this.isDragging && !this.grid.isDraggingRect && !event.shiftKey && !event.metaKey && !event.ctrlKey && this.grid.selectedSlides.length > 1 && event.which !== 3) {
            this.grid.selectSlide(this, false, false, true);
        }
    },

    onDoubleClick: function(evemt) {
        this.grid.closeSlideGrid();
    },

    onContextMenu: function(event) {
        this.grid.selectSlide(this, false, false);
        this.grid.showContextMenu(event, this.$el);
    },

});

export { SlideGrid };
